import { Link, useParams } from "react-router-dom";
import { ContextBar } from "../components/ContextBar";
import { Placeholder } from "../components/Placeholder";
import { ROUTES } from "../lib/routes";
import { getLab, getLabsByDept } from "../lib/labs";
import { resolveDepartment } from "../lib/departments";
import { useScrollPastHero } from "../lib/useScrollPastHero";

export default function LabDetail() {
  const { slug = "" } = useParams<{ slug: string }>();
  const lab = getLab(slug);
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();

  if (!lab) {
    return (
      <section className="center-hero">
        <div className="container">
          <div className="ch-eyebrow">404 · Laboratory</div>
          <h1 className="ch-name">Laboratory not found.</h1>
          <p style={{ color: "rgba(255,255,255,0.7)" }}>
            <Link to={ROUTES.academics} style={{ color: "var(--gold-400)" }}>← Back to academics</Link>
          </p>
        </div>
      </section>
    );
  }

  const dept = resolveDepartment(lab.departmentCode);
  const deptHref = `${ROUTES.department}?dept=${lab.departmentCode}`;
  const siblingLabs = getLabsByDept(lab.departmentCode).filter((l) => l.slug !== lab.slug).slice(0, 3);

  return (
    <article>
      <ContextBar
        visible={scrolledPast}
        tag={`LAB · ${lab.short}`}
        title={lab.name}
        back={dept ? { label: `${dept.code} department`, to: deptHref } : { label: "Academics", to: ROUTES.academics }}
      />
      <section className="center-hero" ref={heroRef}>
        <div className="container">
          <nav className="event-crumbs" aria-label="Breadcrumb">
            <Link to={ROUTES.home}>Home</Link>
            <span aria-hidden>/</span>
            <Link to={ROUTES.academics}>Academics</Link>
            <span aria-hidden>/</span>
            {dept && (
              <>
                <Link to={deptHref}>{dept.code}</Link>
                <span aria-hidden>/</span>
              </>
            )}
            <span>Labs</span>
          </nav>
          <div className="ch-tagrow">
            <span className="ch-short">{lab.short}</span>
            <span className="ch-eyebrow">
              {dept ? dept.name : "Laboratory"}{lab.established ? ` · Established ${lab.established}` : ""}
            </span>
          </div>
          <h1 className="ch-name">{lab.name}</h1>
          <p className="ch-summary">{lab.summary}</p>
        </div>
      </section>

      {/* Gallery */}
      <section className="section section-tight">
        <div className="container">
          <div className="lab-gallery">
            {lab.gallery.slice(0, 3).map((g, i) => (
              <div key={i} className="lab-shot">
                <Placeholder label={g} aspect="4/3" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="detail-shell">
        <div className="container">
          <div className="detail-shell-grid">
            <div className="detail-body">
              <h2>About the lab</h2>
              {lab.intro.map((p, i) => <p key={i}>{p}</p>)}

              <h2>Major equipment</h2>
              <div className="lab-spec-grid">
                {lab.equipment.map((e, i) => (
                  <div key={i} className="lab-spec">
                    <div className="ls-name">{e.name}</div>
                    <div className="ls-detail">{e.detail}</div>
                  </div>
                ))}
              </div>

              <h2>Software &amp; licenses</h2>
              <div className="lab-spec-grid">
                {lab.software.map((s, i) => (
                  <div key={i} className="lab-spec">
                    <div className="ls-name">{s.name}</div>
                    <div className="ls-detail">{s.detail}</div>
                  </div>
                ))}
              </div>
            </div>

            <aside className="detail-meta-card is-accented">
              <div className="dmc-block">
                <div className="dmc-label">Department</div>
                <div className="dmc-value">{dept ? <Link to={deptHref}>{dept.name}</Link> : lab.departmentCode}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Location</div>
                <div className="dmc-value">{lab.location}</div>
              </div>
              {lab.incharge && (
                <div className="dmc-block">
                  <div className="dmc-label">Lab in-charge</div>
                  <div className="dmc-value">{lab.incharge}</div>
                </div>
              )}
              {lab.capacity && (
                <div className="dmc-block">
                  <div className="dmc-label">Capacity</div>
                  <div className="dmc-value">{lab.capacity}</div>
                </div>
              )}
              {lab.established && (
                <div className="dmc-block">
                  <div className="dmc-label">Established</div>
                  <div className="dmc-value">{lab.established}</div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>

      {siblingLabs.length > 0 && (
        <section className="event-related">
          <div className="container">
            <div className="er-head">
              <div className="kicker">{dept ? dept.code : ""} laboratories</div>
              <h2>Other labs in the department</h2>
            </div>
            <div className="er-grid lab-related">
              {siblingLabs.map((o) => (
                <Link key={o.slug} to={`${ROUTES.labDetail}/${o.slug}`} className="er-card">
                  <div className="er-body">
                    <span className="er-tag">{o.short}</span>
                    <h3>{o.name}</h3>
                    <span className="er-venue">{o.location}</span>
                  </div>
                  <span className="er-arr" aria-hidden>→</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
